<template>
  <a-button
    ref="buttonRef"
    :type="variant"
    :loading="submitting"
    class="aha-antd-button"
    :class="[
      {
        'btn-underline': underline,
        'disabled': disabled,
        'is-loading-inline': isLoadingInline,
        'has-icon': iconName.length && !submitting,
        'has-icon-only': iconName.length && !($slots.default && $slots.default.length),
        'active': active
      },
      `ant-btn-${variant}`,
      `ant-btn-${size}`,
      'ant-btn-custom'

    ]"
    :disabled="disabled"
    :ghost="ghost"
    :icon="antIcon"
    :block="block"
    @click="handleClick"
  >
    <aha-icon
      :name="iconName"
      :height="iconSize"
      :width="iconSize"
      :viewBox="iconViewBox"
      :maintainColor="iconMaintainColor"
      :class="iconClasses"
      v-if="iconName.length && !submitting"
    />
    <div v-if="$slots.default && $slots.default.length" class="--content-container" :class="{ 'invisible': isLoadingInline && submitting }">
      <slot />
    </div>
  </a-button>
</template>

<script>
import { Button } from 'ant-design-vue'
import { ICON } from '../../../utils/constants'

export default {
  components: {
    'a-button': Button,
  },
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator(value) {
        return ['primary', 'secondary', 'tertiary', 'success', 'danger', 'link', 'essential', 'pro', 'text', 'text-link', 'primary-alt'].includes(value)
      },
    },
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['sm', 'md', 'lg', 'xl', '2xl'].includes(value)
      },
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    iconName: {
      type: String,
      default: '',
    },
    block: {
      type: Boolean,
      default: false,
    },
    underline: {
      type: Boolean,
      default: false,
    },
    iconViewBox: {
      type: String,
      default: '0 0 24 24',
    },
    iconClasses: {
      type: String,
      default: '',
    },
    isLoadingInline: {
      type: Boolean,
      default: false,
    },
    ghost: {
      type: Boolean,
      default: false,
    },
    antIcon: {
      type: String,
      default: '',
    },
    iconMaintainColor: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    iconSize() {
      return this.size === 'sm' || this.size === 'md' ? ICON.MD_SIZE : ICON.LG_SIZE
    },
  },
  methods: {
    handleClick(event) {
      this.$emit('click', event)

      // Always remove focus after click to prevent stuck focus state
      // :focus-visible will still work for keyboard navigation
      this.$nextTick(() => {
        if (this.$refs.buttonRef?.el && !this.submitting && !this.disabled) {
          this.$refs.buttonRef.$el.blur()
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.aha-antd-button {
  @apply flex justify-center items-center gap-2;

  /* Primary Button Colors */
  --btn-primary-bg: theme('colors.purple.60');
  --btn-primary-text: theme('colors.gray.0');
  --btn-primary-border: theme('colors.purple.60');
  --btn-primary-hover-bg: theme('colors.purple.50');
  --btn-primary-hover-border: theme('colors.purple.50');
  --btn-primary-active-bg: theme('colors.purple.80');
  --btn-primary-active-border: theme('colors.purple.80');
  --btn-primary-focus-shadow: theme('colors.purple.30');

  /* Primary Alt Button Colors */
  --btn-primary-alt-bg: theme('colors.purple.60');
  --btn-primary-alt-text: theme('colors.gray.0');
  --btn-primary-alt-border: theme('colors.purple.90');
  --btn-primary-alt-hover-bg: theme('colors.purple.50');
  --btn-primary-alt-hover-border: theme('colors.purple.90');
  --btn-primary-alt-active-bg: theme('colors.purple.80');
  --btn-primary-alt-active-border: theme('colors.purple.80');
  --btn-primary-alt-focus-shadow: theme('colors.purple.30');

  /* Secondary Button Colors */
  --btn-secondary-bg: theme('colors.gray.0');
  --btn-secondary-text: theme('colors.gray.100');
  --btn-secondary-border: theme('colors.gray.40');
  --btn-secondary-hover-bg: theme('colors.purple.10');
  --btn-secondary-hover-text: theme('colors.purple.50');
  --btn-secondary-hover-border: theme('colors.purple.30');
  --btn-secondary-active-bg: theme('colors.purple.15');
  --btn-secondary-active-text: theme('colors.purple.60');
  --btn-secondary-active-border: theme('colors.purple.40');

  /* Tertiary Button Styles */
  --btn-tertiary-bg: transparent;
  --btn-tertiary-text: theme('colors.gray.100');
  --btn-tertiary-border: transparent;
  --btn-tertiary-hover-bg: theme('colors.purple.10');
  --btn-tertiary-hover-text: theme('colors.purple.50');
  --btn-tertiary-hover-border: theme('colors.purple.10');
  --btn-tertiary-active-bg: theme('colors.purple.15');
  --btn-tertiary-active-text: theme('colors.purple.60');
  --btn-tertiary-active-border: theme('colors.purple.15');

   /* Essential Button Styles */
  --btn-essential-bg: theme('colors.coral.50');
  --btn-essential-active-bg: theme('colors.coral.60');
  --btn-essential-hover-bg: theme('colors.coral.40');

   /* Pro Button Styles */
  --btn-pro-bg: theme('colors.emerald.60');
  --btn-pro-active-bg: theme('colors.emerald.70');
  --btn-pro-hover-bg: theme('colors.emerald.50');

  /* Disabled Button Colors */
  --btn-disabled-bg: theme('colors.gray.40');
  --btn-disabled-text: theme('colors.gray.60');
  --btn-disabled-border: theme('colors.gray.40');

  /* Focus State */
  --btn-focus-shadow-size: 0px 0px 0px 4px;
}

.ant-btn-custom {
  @apply relative border-0;

  &.disabled {
    color: var(--btn-disabled-text);
    @apply cursor-not-allowed;
  }

  /* Remove all :focus styles, only keep :focus-visible for keyboard navigation */
  &:focus {
    box-shadow: none;
    outline: none;
  }

  &:focus-visible {
    border-color: transparent;
    box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-focus-shadow);
    outline: none;
  }

  &.ant-btn-secondary  {
    font-weight: 600;
    border: 1px solid var(--btn-secondary-border);
    color: var(--btn-secondary-text);
    background-color: var(--btn-secondary-bg);

    &.disabled {
      border-color: var(--btn-disabled-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-disabled-bg);

      &:hover, &:active, &:focus {
        border-color: var(--btn-disabled-border);
        color: var(--btn-disabled-text);
        background-color: var(--btn-disabled-bg);
        box-shadow: none;
      }
    }

    &:not(.disabled) {
      &:active, &.active {
        border-color: var(--btn-secondary-active-border);
        color: var(--btn-secondary-active-text);
        background-color: var(--btn-secondary-active-bg);
      }
      &:hover {
        border-color: var(--btn-secondary-hover-border);
        background-color: var(--btn-secondary-hover-bg);
        color: var(--btn-secondary-hover-text);
      }
      &:focus-visible {
        color: var(--btn-secondary-text);
        border-color: transparent;
        background-color: var(--btn-secondary-bg);
        box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-focus-shadow);
      }
    }

    &.ant-btn-loading {
      border-color: var(--btn-secondary-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-secondary-bg);
    }
  }

  &.ant-btn-success  {
    font-weight: 600;
    background-color: theme('colors.button.successBg');
    color: theme('colors.button.successText');

    &:active {
      background-color: theme('colors.button.successBgActive');
    }
    &:hover {
      background-color: theme('colors.button.successBgHover');
      color: theme('colors.button.text');
    }

    &:focus-visible {
      border: none;
    }
  }

  &.ant-btn-primary {
    font-weight: 600;
    border: 1px solid var(--btn-primary-border);
    color: var(--btn-primary-text);
    background-color: var(--btn-primary-bg);

    &.disabled {
      border-color: var(--btn-disabled-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-disabled-bg);

      &:hover, &:active, &:focus {
        border-color: var(--btn-disabled-border);
        color: var(--btn-disabled-text);
        background-color: var(--btn-disabled-bg);
        box-shadow: none;
      }
    }

    &:not(.disabled) {
      &:hover {
        border-color: var(--btn-primary-hover-border);
        background-color: var(--btn-primary-hover-bg);
        color: var(--btn-primary-text);
      }
      &:active, &.active {
        border-color: var(--btn-primary-active-border);
        background-color: var(--btn-primary-active-bg);
        color: var(--btn-primary-text);
      }
      &:focus-visible {
        border-color: transparent;
        background-color: var(--btn-primary-bg);
        color: var(--btn-primary-text);
        box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-focus-shadow);
      }
    }

    &.ant-btn-loading {
      border-color: var(--btn-primary-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-primary-bg);
    }
  }

  &.ant-btn-primary-alt {
    font-weight: 600;
    border: 1px solid var(--btn-primary-alt-border);
    color: var(--btn-primary-alt-text);
    background-color: var(--btn-primary-alt-bg);

    &.disabled {
      border-color: var(--btn-disabled-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-disabled-bg);

      &:hover, &:active, &:focus {
        border-color: var(--btn-disabled-border);
        color: var(--btn-disabled-text);
        background-color: var(--btn-disabled-bg);
        box-shadow: none;
      }
    }

    &:not(.disabled) {
      &:hover {
        border-color: var(--btn-primary-alt-hover-border);
        background-color: var(--btn-primary-alt-hover-bg);
        color: var(--btn-primary-alt-text);
      }
      &:active, &.active {
        border-color: var(--btn-primary-alt-active-border);
        background-color: var(--btn-primary-alt-active-bg);
        color: var(--btn-primary-alt-text);
      }
      &:focus-visible {
        border-color: transparent;
        background-color: var(--btn-primary-alt-bg);
        color: var(--btn-primary-alt-text);
        box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-alt-focus-shadow);
      }
    }

    &.ant-btn-loading {
      border-color: var(--btn-primary-alt-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-primary-alt-bg);
    }
  }

  &.ant-btn-link {
    color: theme('colors.primary.90');
    &:hover {
      color: theme('colors.purple.40');
    }
    &:active {
      color: theme('colors.primary.100');
    }
  }
  &.ant-btn-danger {
    font-weight: 600;
    background-color: #D43F48;
    color: theme('colors.button.dangerText');

    &:hover {
      background-color: theme('colors.button.dangerBgHover');
      color: theme('colors.button.dangerText');
    }

  }
  &.ant-btn-tertiary {
    font-weight: 600;
    border: none;
    box-shadow: none;
    color: var(--btn-tertiary-text);
    background-color: var(--btn-tertiary-bg);

    &.disabled {
      color: var(--btn-disabled-text);
      background-color: transparent;

      &:hover, &:active, &:focus {
        color: var(--btn-disabled-text);
        background-color: transparent;
        box-shadow: none;
      }
    }

    &:not(.disabled) {
      &:hover {
        border-color: var(--btn-tertiary-hover-border);
        background-color: var(--btn-tertiary-hover-bg);
        color: var(--btn-tertiary-hover-text);
      }
      &:active, &.active {
        border-color: var(--btn-tertiary-active-border);
        background-color: var(--btn-tertiary-active-bg);
        color: var(--btn-tertiary-active-text);
      }
      &:focus-visible {
        border-color: transparent;
        background-color: var(--btn-tertiary-bg);
        color: var(--btn-tertiary-text);
        box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-focus-shadow);
      }
    }

    &.ant-btn-loading {
      border-color: var(--btn-tertiary-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-tertiary-bg);
    }
  }

  &.ant-btn-text {
    border: none;
    box-shadow: none;
    &:hover {
      color: unset;
      background-color: #E0E3ED;
    }
    &:active {
      color: unset;
      background-color: #D4D7E0;
    }

    &:focus-visible {
      color: unset;
    }
  }
  &.ant-btn-text-link {
    border: none;
    box-shadow: none;
    color: theme('colors.primary.90');

    &:hover {
      color: theme('colors.purple.40');
    }
    &:active {
      color: theme('colors.primary.100');
    }
    &:focus-visible {
      color: theme('colors.primary.90');
    }
  }
  &.ant-btn-essential {
    font-weight: 600;
    background-color: var(--btn-essential-bg);
    color: var(--btn-primary-text);

    &:active {
      background-color: var(--btn-essential-active-bg);
    }
    &:hover {
      background-color: var(--btn-essential-hover-bg);
      color: var(--btn-primary-text);
    }
  }

  &.ant-btn-pro {
    font-weight: 600;
    background-color: var(--btn-pro-bg);
    color: var(--btn-primary-text);

    &:active {
      background-color: var(--btn-pro-active-bg);
    }
    &:hover {
      background-color: var(--btn-pro-hover-bg);
      color: var(--btn-primary-text);
    }
  }

  .--content-container {
    line-height: 1.5;
  }

  .disabled {
    cursor: not-allowed;
  }
}

.is-loading-inline {
  ::v-deep {
    .anticon {
      position: absolute;
      left: 0;
      right: 0;
      margin: auto !important;
      width: 16px;
    }
  }
}

::v-deep {
  .anticon {
    width: 16px;
  }
}

.btn-underline {
  ::v-deep {
    span {
      text-decoration: underline;
    }
  }
}
.ant-btn-sm {
  height: 28px;
  padding: 4px 8px;
  border-radius: 4px;
  gap: 4px;

  &.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 20px;
  }

  &.has-icon {
    padding-left: 6px;
  }
  &.has-icon-only {
    padding-left: 5px;
    padding-right: 5px;
  }
  &.is-loading-inline.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 8px;
  }
}
.ant-btn-md {
  height: 36px;
  padding: 8px 16px;
  border-radius: 8px;

  &.has-icon {
    padding-left: 14px;
  }

  &.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 28px;
  }

  &.has-icon-only {
    padding-left: 9px;
    padding-right: 9px;
  }

  &.is-loading-inline.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 16px;
  }
}
.ant-btn-lg {
  height: 44px;
  padding: 12px 20px;
  border-radius: 8px;

  &.has-icon {
    padding-left: 18px;
  }

  &.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 36px;
  }
  &.has-icon-only {
    padding-left: 11px;
    padding-right: 11px;
  }
  &.is-loading-inline.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 20px;
  }
}
.ant-btn-xl {
  height: 52px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 16px;

  &.has-icon {
    padding-left: 18px;
  }

  &.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 36px;
  }
  &.has-icon-only {
    padding-left: 15px;
    padding-right: 15px;
  }

  &.is-loading-inline.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 20px;
  }
}
</style>
